import { ComponentType } from 'react';
import { IndexableObject } from '../utils/types/ObjectTypes';

type WrapperComponent = (Function | object) &
  IndexableObject & {
    WrappedComponent?: WrapperComponent;
  };

export type DecoratorComponent = WrapperComponent & {
  CoreComponent?: WrapperComponent;
};

export function getCoreComponent(
  Component: WrapperComponent
): WrapperComponent {
  let node = Component;
  while (node.WrappedComponent) {
    node = node.WrappedComponent;
  }

  return node;
}

export function isFunctionalComponent<T>(Component: ComponentType<T>): boolean {
  return !Component.prototype || !Component.prototype.render;
}

export function attachWrappedComponent(
  Decorator: DecoratorComponent,
  WrapperComponent: WrapperComponent
): void {
  Decorator.WrappedComponent = WrapperComponent;
  Decorator.CoreComponent = getCoreComponent(WrapperComponent);
}

export function copyMethods(
  wrappedInstance: WrapperComponent,
  decoratorInstance: DecoratorComponent
): void {
  // Copy all methods from the wrapped component instance to this wrapper,
  // so that this wrapper is usable as a ref.
  if (!wrappedInstance) {
    return;
  }
  Object.getOwnPropertyNames(wrappedInstance)
    .filter((key) => key !== 'getDOMNode') // See #4515
    .forEach((key) => {
      if (
        typeof wrappedInstance[key] === 'function' &&
        !decoratorInstance[key]
      ) {
        decoratorInstance[key] = wrappedInstance[key];
      }
    });
}

export type DecoratedRefCallback =
  | ((ref: WrapperComponent) => void)
  | undefined;

export const makeDecoratorRefCallback = <T>(
  Component: ComponentType<T>,
  decoratorInstance: DecoratorComponent
): DecoratedRefCallback => {
  if (isFunctionalComponent(Component)) return undefined;
  return (ref: WrapperComponent) => {
    copyMethods(ref, decoratorInstance);
  };
};
